import type { EnemyRoleId } from "../content/schemas";
import {
  KILL_STREAK_BONUS_PER,
  MAX_RUN_LEVEL,
  SALVAGE_XP_DIVISOR,
  TIME_XP_AMOUNT,
  TIME_XP_INTERVAL_S,
  XP_LEVEL_BASE,
  XP_LEVEL_EXPONENT,
  XP_LEVEL_LINEAR,
} from "./constants";
import type { WeaponModInstance } from "./weapon-mods";

export type RunProgress = {
  xp: number;
  level: number;
  xpToNext: number;
  unspentSkillPoints: number;
  takenNodes: string[];
  /** Stacked weapon mods attached to equipped slots. */
  weaponMods: WeaponModInstance[];
  score: number;
  killStreak: number;
  bestKillStreak: number;
  bestChain: number;
  levelUpPending: boolean;
  /** Three skill / weapon-mod offer ids while picking; empty when not picking. */
  skillPickOffers: string[];
  /** Nodes removed from future offers this run (banish). */
  banishedNodes: string[];
  /** Rerolls consumed this run. */
  rerollsUsed: number;
  lastTimeXpAt: number;
};

export const XP_BY_ROLE: Record<EnemyRoleId, number> = {
  scout: 8,
  skirmisher: 14,
  turret: 18,
  bomber: 28,
  jammer: 16,
  breacher: 20,
  carrier: 32,
  sniper: 36,
  scavenger: 34,
  marauder: 44,
  tender: 42,
  sapper: 52,
  nullifier: 60,
  leech: 68,
  warden: 120,
  harvester: 180,
  sovereign: 260,
  leviathan: 340,
  null_crown: 450,
  centurion: 600,
  ace: 48,
};

export const SCORE_BY_ROLE: Record<EnemyRoleId, number> = {
  scout: 100,
  skirmisher: 175,
  turret: 225,
  bomber: 350,
  jammer: 200,
  breacher: 240,
  carrier: 420,
  sniper: 480,
  scavenger: 450,
  marauder: 620,
  tender: 560,
  sapper: 720,
  nullifier: 860,
  leech: 980,
  warden: 1800,
  harvester: 2600,
  sovereign: 3800,
  leviathan: 5200,
  null_crown: 7000,
  centurion: 9500,
  ace: 650,
};

export const BOSS_ARENA_XP = 120;
export const SIEGE_WARDEN_XP = 240;
export const DREAD_HARVESTER_XP = 360;
export const VELOCITY_SOVEREIGN_XP = 520;
export const FOUNDRY_LEVIATHAN_XP = 680;
export const NULL_CROWN_XP = 900;
export const CENTURION_XP = 1200;
export const ELITE_XP_MULT = 2.5;
export const ELITE_SCORE_MULT = 2;

export function xpRequiredForLevel(level: number): number {
  return Math.floor(XP_LEVEL_BASE * level ** XP_LEVEL_EXPONENT + XP_LEVEL_LINEAR * level);
}

export function createRunProgress(): RunProgress {
  return {
    xp: 0,
    level: 1,
    xpToNext: xpRequiredForLevel(1),
    unspentSkillPoints: 0,
    takenNodes: [],
    weaponMods: [],
    score: 0,
    killStreak: 0,
    bestKillStreak: 0,
    bestChain: 0,
    levelUpPending: false,
    skillPickOffers: [],
    banishedNodes: [],
    rerollsUsed: 0,
    lastTimeXpAt: 0,
  };
}

export function cloneRunProgress(progress: RunProgress): RunProgress {
  return {
    ...progress,
    takenNodes: [...progress.takenNodes],
    weaponMods: progress.weaponMods.map((m) => ({ ...m })),
    skillPickOffers: [...progress.skillPickOffers],
    banishedNodes: [...progress.banishedNodes],
  };
}

/** Apply XP and handle level-ups. Returns number of levels gained. */
export function grantXp(progress: RunProgress, amount: number): number {
  if (amount <= 0 || progress.level >= MAX_RUN_LEVEL) return 0;

  progress.xp += amount;
  let levelsGained = 0;

  while (progress.level < MAX_RUN_LEVEL && progress.xp >= progress.xpToNext) {
    progress.xp -= progress.xpToNext;
    progress.level += 1;
    progress.unspentSkillPoints += 1;
    progress.levelUpPending = true;
    progress.xpToNext = progress.level >= MAX_RUN_LEVEL ? 0 : xpRequiredForLevel(progress.level);
    levelsGained += 1;
  }

  if (progress.level >= MAX_RUN_LEVEL) {
    progress.xp = 0;
    progress.xpToNext = 0;
  }

  return levelsGained;
}

export function xpForEnemyKill(role: EnemyRoleId, elite: boolean): number {
  const base = XP_BY_ROLE[role];
  return Math.round(base * (elite ? ELITE_XP_MULT : 1));
}

export function scoreForEnemyKill(
  progress: RunProgress,
  role: EnemyRoleId,
  elite: boolean,
): number {
  const streakMult = 1 + progress.killStreak * KILL_STREAK_BONUS_PER;
  const base = SCORE_BY_ROLE[role] * (elite ? ELITE_SCORE_MULT : 1);
  return Math.round(base * streakMult);
}

export function recordEnemyKill(
  progress: RunProgress,
  role: EnemyRoleId,
  elite: boolean,
): { xpGained: number; levelsGained: number } {
  progress.killStreak += 1;
  progress.bestKillStreak = Math.max(progress.bestKillStreak, progress.killStreak);

  const xpGained = xpForEnemyKill(role, elite);
  const levelsGained = grantXp(progress, xpGained);

  return { xpGained, levelsGained };
}

export function resetKillStreak(progress: RunProgress): void {
  progress.killStreak = 0;
}

export function xpForSalvagePickup(amount: number): number {
  return Math.floor(amount / SALVAGE_XP_DIVISOR);
}

export function awardTimeSurvivedXp(
  progress: RunProgress,
  simTime: number,
): { xp: number; intervals: number } {
  if (simTime - progress.lastTimeXpAt < TIME_XP_INTERVAL_S) return { xp: 0, intervals: 0 };
  const intervals = Math.floor((simTime - progress.lastTimeXpAt) / TIME_XP_INTERVAL_S);
  if (intervals <= 0) return { xp: 0, intervals: 0 };
  progress.lastTimeXpAt += intervals * TIME_XP_INTERVAL_S;
  const xp = intervals * TIME_XP_AMOUNT;
  grantXp(progress, xp);
  return { xp, intervals };
}
